import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area,
  Legend, LineChart, Line,
} from 'recharts';
import { claims } from '../services/api.js';
import './ClaimsDashboard.css';

const BRAND_COLORS = ['#3b82f6', '#0bc1ff', '#1a6db0', '#06a3d9', '#3b5998', '#4cc9f0', '#1d3557', '#48bfe3'];
const STATUS_COLORS = { closed: '#10b981', open: '#f59e0b', reserved: '#ef4444' };

/* ── AI Litigation hardcoded data ── */
const GENAI_CASES_BY_CATEGORY = [
  { name: 'Copyright / IP', cases: 52 },
  { name: 'Employment / Hiring Bias', cases: 12 },
  { name: 'Privacy / Data', cases: 9 },
  { name: 'Healthcare AI', cases: 8 },
  { name: 'Financial Services', cases: 6 },
  { name: 'Defamation / Content', cases: 5 },
  { name: 'Other', cases: 5 },
];

const GENAI_CASES_OVER_TIME = [
  { quarter: 'Q1 2024', filings: 8 },
  { quarter: 'Q2 2024', filings: 12 },
  { quarter: 'Q3 2024', filings: 15 },
  { quarter: 'Q4 2024', filings: 18 },
  { quarter: 'Q1 2025', filings: 22 },
  { quarter: 'Q2 2025', filings: 28 },
  { quarter: 'Q3 2025', filings: 32 },
  { quarter: 'Q4 2025', filings: 35 },
  { quarter: 'Q1 2026', filings: 40 },
];

const GENAI_SEVERITY_BY_CATEGORY = [
  { name: 'Healthcare', avg: 50000000 },
  { name: 'Copyright', avg: 15000000 },
  { name: 'Financial', avg: 12000000 },
  { name: 'Hiring', avg: 8000000 },
  { name: 'Privacy', avg: 5000000 },
];

const GENAI_EXPOSURE_TAGS = [
  { category: 'Copyright / IP', tags: 'EXP14GENAI (IP Content), EXP04GENAI (Unverified Output)', scores: '+4, +4' },
  { category: 'Hiring Bias', tags: 'EXP01GENAI (High Stakes Decision), EXP02GENAI (Action Triggering)', scores: '+5, +5' },
  { category: 'Healthcare AI', tags: 'EXP01GENAI, EXP08GENAI (Sensitive Data), EXP06GENAI (Regulated)', scores: '+5, +5, +3' },
  { category: 'Financial', tags: 'EXP01GENAI, EXP08GENAI, EXP06GENAI', scores: '+5, +5, +3' },
  { category: 'Privacy', tags: 'EXP16GENAI (Unauthorized Disclosure), EXP08GENAI', scores: '+5, +5' },
];

const GENAI_NOTABLE_CASES = [
  { case: 'Concord Music v. Anthropic', category: 'Copyright', year: 2026, parties: 'Music publishers v. Anthropic', status: 'Pending', exposure: '$50M+' },
  { case: 'Disney v. Midjourney', category: 'Copyright', year: 2025, parties: 'Disney v. Midjourney', status: 'Pending', exposure: '$100M+' },
  { case: 'Mobley v. Workday', category: 'Hiring Bias', year: 2024, parties: 'Applicant v. Workday', status: 'Pending (EEOC support)', exposure: '$50M+ class' },
  { case: 'Harper v. Sirius XM', category: 'Hiring Bias', year: 2025, parties: 'Applicant v. Sirius XM', status: 'Pending', exposure: '$25M+ class' },
  { case: 'Lokken v. UnitedHealth', category: 'Healthcare', year: 2023, parties: 'Patients v. UHG', status: 'Proceeding', exposure: '$1B+ class' },
  { case: 'iTutorGroup (EEOC)', category: 'Hiring Bias', year: 2023, parties: 'EEOC v. iTutorGroup', status: 'Settled: $365K', exposure: '$365K' },
  { case: 'MA v. Earnest Operations', category: 'Financial', year: 2025, parties: 'Mass AG v. Earnest', status: 'Settled: $2.5M', exposure: '$2.5M' },
  { case: 'Thomson Reuters v. ROSS', category: 'Copyright', year: 2025, parties: 'TR v. ROSS Intelligence', status: 'Plaintiff won', exposure: 'Undisclosed' },
  { case: 'Carryrou et al v. Multiple', category: 'Copyright', year: 2025, parties: '6 journalists v. AI cos', status: 'Pending', exposure: '$10M+' },
  { case: 'Lyon v. Adobe', category: 'Copyright', year: 2025, parties: 'Class v. Adobe', status: 'Pending', exposure: '$25M+' },
];

const TYPE_LABELS = {
  bec: 'BEC',
  ftf: 'Funds Transfer Fraud',
  ransomware: 'Ransomware',
  data_breach: 'Data Breach',
  system_failure: 'System Failure',
  other: 'Other',
};

function formatCurrency(val) {
  if (val == null) return '—';
  if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
  return `$${val.toLocaleString()}`;
}

function formatFullCurrency(val) {
  if (val == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="cd__tooltip">
      <div className="cd__tooltip-label">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="cd__tooltip-item" style={{ color: p.color }}>
          {p.name}: {typeof p.value === 'number' && p.name?.toLowerCase().includes('amount')
            ? formatFullCurrency(p.value)
            : typeof p.value === 'number' && p.value > 1000
              ? formatFullCurrency(p.value)
              : p.value}
        </div>
      ))}
    </div>
  );
}

function GenAILitigationTab() {
  return (
    <>
      {/* Demo data banner */}
      <div className="cd__demo-banner">
        <span className="cd__demo-banner-icon">&#9432;</span>
        <span>This tab uses curated research data for demonstration purposes. A backend data source for litigation tracking is not yet connected.</span>
      </div>

      {/* Stats Bar */}
      <div className="cd__stats">
        <div className="card cd__stat-card">
          <span className="cd__stat-label">Total Cases</span>
          <span className="cd__stat-value">97+</span>
        </div>
        <div className="card cd__stat-card">
          <span className="cd__stat-label">Active Cases</span>
          <span className="cd__stat-value" style={{ color: '#f59e0b' }}>~65</span>
        </div>
        <div className="card cd__stat-card">
          <span className="cd__stat-label">Settlements YTD</span>
          <span className="cd__stat-value">4</span>
        </div>
        <div className="card cd__stat-card">
          <span className="cd__stat-label">Est. Aggregate Exposure</span>
          <span className="cd__stat-value" style={{ color: '#ef4444' }}>$2B+</span>
        </div>
      </div>

      {/* Charts */}
      <div className="cd__charts">
        {/* Cases by Category */}
        <div className="card cd__chart-card">
          <h3 className="cd__chart-title">Cases by Category</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={GENAI_CASES_BY_CATEGORY} layout="vertical" margin={{ left: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="cases" name="Cases" fill="#3b82f6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Severity by Category */}
        <div className="card cd__chart-card">
          <h3 className="cd__chart-title">Avg. Potential Exposure by Category</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={GENAI_SEVERITY_BY_CATEGORY} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis type="number" tickFormatter={formatCurrency} />
              <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="avg" name="Avg Exposure" fill="#0bc1ff" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Cases Over Time */}
        <div className="card cd__chart-card cd__chart-card--full">
          <h3 className="cd__chart-title">AI Litigation Filings Over Time</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={GENAI_CASES_OVER_TIME}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="quarter" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="filings"
                name="Case Filings"
                stroke="#3b82f6"
                strokeWidth={2.5}
                dot={{ fill: '#3b82f6', r: 4 }}
                activeDot={{ r: 6, fill: '#0bc1ff' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Exposure Tags Mapping */}
      <div className="card cd__table-section">
        <h3 className="cd__table-title" style={{ marginBottom: 'var(--space-md)' }}>
          Mapping to AI Exposure Tags
        </h3>
        <table className="cd__table">
          <thead>
            <tr>
              <th>Case Category</th>
              <th>Triggered Exposure Tags</th>
              <th>Score Impact</th>
            </tr>
          </thead>
          <tbody>
            {GENAI_EXPOSURE_TAGS.map((row) => (
              <tr key={row.category}>
                <td><span className="cd__type-badge">{row.category}</span></td>
                <td className="cd__tag-cell">
                  {row.tags.split(', ').map((tag) => (
                    <span key={tag} className="cd__exposure-tag">{tag}</span>
                  ))}
                </td>
                <td className="cd__score-cell">
                  {row.scores.split(', ').map((s, i) => (
                    <span key={i} className="cd__score-badge">{s}</span>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Notable Cases Table */}
      <div className="card cd__table-section">
        <h3 className="cd__table-title" style={{ marginBottom: 'var(--space-md)' }}>
          Notable AI Litigation Cases
        </h3>
        <table className="cd__table">
          <thead>
            <tr>
              <th>Case</th>
              <th>Category</th>
              <th>Year</th>
              <th>Parties</th>
              <th>Status</th>
              <th>Est. Exposure</th>
            </tr>
          </thead>
          <tbody>
            {GENAI_NOTABLE_CASES.map((c) => (
              <tr key={c.case}>
                <td style={{ fontWeight: 600 }}>{c.case}</td>
                <td><span className="cd__type-badge">{c.category}</span></td>
                <td>{c.year}</td>
                <td>{c.parties}</td>
                <td>
                  <span className={`cd__status ${
                    c.status.startsWith('Settled') ? 'cd__status--closed'
                    : c.status === 'Plaintiff won' ? 'cd__status--closed'
                    : c.status === 'Proceeding' ? 'cd__status--reserved'
                    : 'cd__status--open'
                  }`}>
                    {c.status}
                  </span>
                </td>
                <td className="cd__amount">{c.exposure}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function ClaimsDashboard() {
  const [activeTab, setActiveTab] = useState('genai');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortKey, setSortKey] = useState('dateOfLoss');
  const [sortDir, setSortDir] = useState('desc');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const res = await claims.list();
        const items = res.data || res.items || res;
        setData(Array.isArray(items) ? items : []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const filtered = useMemo(() => {
    let items = [...data];
    if (filterType) items = items.filter((c) => c.claimType === filterType);
    if (filterStatus) items = items.filter((c) => c.status === filterStatus);
    return items;
  }, [data, filterType, filterStatus]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let va = a[sortKey];
      let vb = b[sortKey];
      if (typeof va === 'number' && typeof vb === 'number') {
        return sortDir === 'asc' ? va - vb : vb - va;
      }
      va = String(va || '');
      vb = String(vb || '');
      return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });
  }, [filtered, sortKey, sortDir]);

  // Stats
  const stats = useMemo(() => {
    const totalIncurred = data.reduce((s, c) => s + (c.incurredAmount || 0), 0);
    const openCount = data.filter((c) => c.status === 'open').length;
    return {
      total: data.length,
      totalIncurred,
      avgSeverity: data.length ? totalIncurred / data.length : 0,
      openCount,
    };
  }, [data]);

  // Chart: Claims by Type
  const byType = useMemo(() => {
    const map = {};
    data.forEach((c) => {
      const label = TYPE_LABELS[c.claimType] || c.claimType;
      if (!map[label]) map[label] = { name: label, count: 0, total: 0 };
      map[label].count += 1;
      map[label].total += c.incurredAmount || 0;
    });
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [data]);

  // Chart: Claims by Industry
  const byIndustry = useMemo(() => {
    const map = {};
    data.forEach((c) => {
      if (!map[c.industry]) map[c.industry] = { name: c.industry, count: 0 };
      map[c.industry].count += 1;
    });
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [data]);

  // Chart: Average Severity by Type
  const severityByType = useMemo(() => {
    const map = {};
    data.forEach((c) => {
      const label = TYPE_LABELS[c.claimType] || c.claimType;
      if (!map[label]) map[label] = { name: label, total: 0, count: 0 };
      map[label].total += c.incurredAmount || 0;
      map[label].count += 1;
    });
    return Object.values(map)
      .map((v) => ({ name: v.name, avgSeverity: Math.round(v.total / v.count) }))
      .sort((a, b) => b.avgSeverity - a.avgSeverity);
  }, [data]);

  // Chart: Claims Over Time (by quarter)
  const overTime = useMemo(() => {
    const map = {};
    data.forEach((c) => {
      if (!c.dateOfLoss) return;
      const d = new Date(c.dateOfLoss);
      const q = Math.ceil((d.getMonth() + 1) / 3);
      const key = `${d.getFullYear()} Q${q}`;
      if (!map[key]) map[key] = { quarter: key, count: 0, totalIncurred: 0 };
      map[key].count += 1;
      map[key].totalIncurred += c.incurredAmount || 0;
    });
    return Object.values(map).sort((a, b) => a.quarter.localeCompare(b.quarter));
  }, [data]);

  // Chart: Revenue Band Distribution
  const byRevenue = useMemo(() => {
    const order = ['<$25M', '$25-100M', '$100-500M', '$500M+'];
    const map = {};
    data.forEach((c) => {
      const band = c.revenueBand || 'Unknown';
      if (!map[band]) map[band] = { name: band, count: 0 };
      map[band].count += 1;
    });
    return order.map((b) => map[b] || { name: b, count: 0 }).filter((b) => b.count > 0);
  }, [data]);

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  }

  function sortArrow(key) {
    if (sortKey !== key) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  }

  if (loading && activeTab === 'cyber') return <div className="cd__loading">Loading claims data...</div>;
  if (error && activeTab === 'cyber') return <div className="cd__error">Error: {error}</div>;

  return (
    <div className="cd">
      <div className="cd__top-bar">
        <h1 className="cd__title">Claims Dashboard</h1>
        <div className="cd__tab-toggle">
          <button
            className={`cd__tab-pill${activeTab === 'cyber' ? ' cd__tab-pill--active' : ''}`}
            onClick={() => setActiveTab('cyber')}
          >
            Cyber Claims
          </button>
          <button
            className={`cd__tab-pill${activeTab === 'genai' ? ' cd__tab-pill--active' : ''}`}
            onClick={() => setActiveTab('genai')}
          >
            AI Litigation
          </button>
        </div>
      </div>

      {activeTab === 'genai' && <GenAILitigationTab />}

      {activeTab === 'cyber' && <>
      {/* Stats Bar */}
      <div className="cd__stats">
        <div className="card cd__stat-card">
          <span className="cd__stat-label">Total Claims</span>
          <span className="cd__stat-value">{stats.total}</span>
        </div>
        <div className="card cd__stat-card">
          <span className="cd__stat-label">Total Incurred</span>
          <span className="cd__stat-value">{formatCurrency(stats.totalIncurred)}</span>
        </div>
        <div className="card cd__stat-card">
          <span className="cd__stat-label">Average Severity</span>
          <span className="cd__stat-value">{formatCurrency(stats.avgSeverity)}</span>
        </div>
        <div className="card cd__stat-card">
          <span className="cd__stat-label">Open Claims</span>
          <span className="cd__stat-value" style={{ color: '#f59e0b' }}>{stats.openCount}</span>
        </div>
      </div>

      {/* Charts */}
      <div className="cd__charts">
        {/* Claims by Type */}
        <div className="card cd__chart-card">
          <h3 className="cd__chart-title">Claims by Type</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={byType} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" name="Count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Claims by Industry */}
        <div className="card cd__chart-card">
          <h3 className="cd__chart-title">Claims by Industry</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={byIndustry} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" name="Count" fill="#0bc1ff" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Severity by Type */}
        <div className="card cd__chart-card">
          <h3 className="cd__chart-title">Average Severity by Type</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={severityByType} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis type="number" tickFormatter={formatCurrency} />
              <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} formatter={(v) => formatFullCurrency(v)} />
              <Bar dataKey="avgSeverity" name="Avg Severity" fill="#1a6db0" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue Band Distribution */}
        <div className="card cd__chart-card">
          <h3 className="cd__chart-title">Claims by Revenue Band</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={byRevenue}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={95}
                dataKey="count"
                nameKey="name"
                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                labelLine={false}
              >
                {byRevenue.map((_, i) => (
                  <Cell key={i} fill={BRAND_COLORS[i % BRAND_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Claims Over Time */}
        <div className="card cd__chart-card cd__chart-card--full">
          <h3 className="cd__chart-title">Claims Over Time (by Quarter)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={overTime}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="quarter" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="count"
                name="Claims"
                stroke="#3b82f6"
                fill="rgba(59, 130, 246, 0.15)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Claims Table */}
      <div className="card cd__table-section">
        <div className="cd__table-header">
          <h3 className="cd__table-title">All Claims ({filtered.length})</h3>
          <div className="cd__filters">
            <select
              className="cd__filter-select"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="">All Types</option>
              {Object.entries(TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <select
              className="cd__filter-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
              <option value="reserved">Reserved</option>
            </select>
          </div>
        </div>

        <table className="cd__table">
          <thead>
            <tr>
              <th onClick={() => handleSort('claimType')}>Type<span className="cd__sort-arrow">{sortArrow('claimType')}</span></th>
              <th>Insured</th>
              <th>Policy #</th>
              <th onClick={() => handleSort('industry')}>Industry<span className="cd__sort-arrow">{sortArrow('industry')}</span></th>
              <th onClick={() => handleSort('incurredAmount')}>Incurred<span className="cd__sort-arrow">{sortArrow('incurredAmount')}</span></th>
              <th onClick={() => handleSort('status')}>Status<span className="cd__sort-arrow">{sortArrow('status')}</span></th>
              <th onClick={() => handleSort('dateOfLoss')}>Date of Loss<span className="cd__sort-arrow">{sortArrow('dateOfLoss')}</span></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((c) => (
              <React.Fragment key={c.claimId || c.SK}>
                <tr
                  onClick={() => setExpandedRow(expandedRow === c.claimId ? null : c.claimId)}
                  style={{ cursor: 'pointer' }}
                >
                  <td><span className="cd__type-badge">{TYPE_LABELS[c.claimType] || c.claimType}</span></td>
                  <td>{c.insuredName || c.InsuredName || '—'}</td>
                  <td>{c.policyNumber || c.PolicyNumber || '—'}</td>
                  <td>{c.industry}</td>
                  <td className="cd__amount">{formatFullCurrency(c.incurredAmount)}</td>
                  <td><span className={`cd__status cd__status--${c.status}`}>{c.status}</span></td>
                  <td>{c.dateOfLoss}</td>
                </tr>
                {expandedRow === c.claimId && (
                  <tr className="cd__desc-row">
                    <td colSpan={7}>{c.description}</td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      </>}
    </div>
  );
}
